import {AnalyticsFactValue, VehicleLogRow, VehicleLogVteMetadata,} from '../../models/vte';
import {extractExpectedProviderIds, isProviderExpected} from './analytics-fact.utils';

export function parseVteMetadata(
    providerMetadata: VehicleLogVteMetadata | Record<string, unknown> | null | undefined,
): VehicleLogVteMetadata {
    if (!providerMetadata || typeof providerMetadata !== 'object') {
        return {};
    }
    return providerMetadata as VehicleLogVteMetadata;
}

export function hasVteTransaction(row: VehicleLogRow | null | undefined): boolean {
    const metadata = parseVteMetadata(row?.provider_metadata);
    return Boolean(metadata.vte_transaction_id);
}

export function getInspectionOutcome(row: VehicleLogRow | null | undefined): string | null {
    const metadata = parseVteMetadata(row?.provider_metadata);
    return metadata.vte_inspection_outcome?.trim() || null;
}

export function getAccessDecision(row: VehicleLogRow | null | undefined): string | null {
    const decision = row?.access_decision;
    return typeof decision === 'string' && decision.trim() ? decision.trim().toUpperCase() : null;
}

/**
 * Whether a provider was expected for this VehicleLog at transaction creation time.
 *
 * Prefer `vte_expected_provider_events` (copy of VT snapshot). For older logs without
 * that field, fall back to correlated / timed-out provider signals. Completed VTE logs
 * with no provider signal treat the capability as not expected (Not Enabled).
 */
export function wasProviderExpectedForVehicleLog(
    row: VehicleLogRow | null | undefined,
    providerId?: string | null,
): boolean | null {
    const provider = (providerId || '').trim();
    if (!provider) {
        return null;
    }
    const metadata = parseVteMetadata(row?.provider_metadata);
    const expectedSnapshot = metadata.vte_expected_provider_events;
    if (Array.isArray(expectedSnapshot)) {
        return isProviderExpected(expectedSnapshot, provider);
    }

    const correlated = new Set(
        (metadata.vte_correlated_provider_ids || [])
            .map((id) => String(id || '').trim())
            .filter(Boolean),
    );
    const timedOutRaw = metadata.vte_correlation_metadata?.['timed_out_providers'];
    const timedOut = new Set(
        (Array.isArray(timedOutRaw) ? timedOutRaw : [])
            .map((id) => String(id || '').trim())
            .filter(Boolean),
    );

    if (correlated.has(provider) || timedOut.has(provider)) {
        return true;
    }

    // Completed VTE-derived log without any expectation signal → historically not applicable.
    if (metadata.vte_transaction_id) {
        return false;
    }

    // Non-VTE / unknown legacy row — do not invent Pending.
    return false;
}

export function expectedProviderIdsForVehicleLog(
    row: VehicleLogRow | null | undefined,
): Set<string> {
    const metadata = parseVteMetadata(row?.provider_metadata);
    if (Array.isArray(metadata.vte_expected_provider_events)) {
        return extractExpectedProviderIds(metadata.vte_expected_provider_events);
    }
    return new Set();
}

function normalizeFactValue(raw: AnalyticsFactValue | null | undefined): {
    value: boolean | number | string | null;
    confidence: number | null;
} | null {
    if (raw === null || raw === undefined) {
        return null;
    }
    if (typeof raw === 'boolean' || typeof raw === 'number' || typeof raw === 'string') {
        return {value: raw, confidence: null};
    }
    if (typeof raw === 'object') {
        let value: boolean | number | string | null = raw.value ?? raw.passed ?? null;
        if ((value === null || value === undefined) && raw.received === true) {
            value = true;
        }
        if (value === null || value === undefined) {
            return null;
        }
        const confidence = typeof raw.confidence === 'number' ? raw.confidence : null;
        return {value, confidence};
    }
    return null;
}

export function extractFactValue(
    row: VehicleLogRow | null | undefined,
    outputFact: string,
): { value: boolean | number | string; confidence: number | null } | null {
    if (!outputFact?.trim()) {
        return null;
    }
    const metadata = parseVteMetadata(row?.provider_metadata);
    const facts = metadata.vte_facts;
    if (!facts || !(outputFact in facts)) {
        return null;
    }
    const normalized = normalizeFactValue(facts[outputFact]);
    if (!normalized || normalized.value === null) {
        return null;
    }
    return {
        value: normalized.value,
        confidence: normalized.confidence,
    };
}

export function rowHasFactValue(row: VehicleLogRow | null | undefined, outputFact: string): boolean {
    return extractFactValue(row, outputFact) !== null;
}

export function anyRowHasFactValue(rows: VehicleLogRow[], outputFact: string): boolean {
    return rows.some((row) => rowHasFactValue(row, outputFact));
}
